import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import {
  displayServerMask,
  formatCount,
  isolateLtr,
  MOBILE_E164_PATTERN,
  normalizeMobileField,
  statusLabel,
} from '@saay/formatting';
import { StatusBadge } from '@saay/blocks';
import { Button as StockButton } from '@saay/shadcn/components/button';
import {
  applyFieldErrors,
  Banner,
  Button,
  Combobox,
  Dialog,
  ErrorState,
  FieldErrorList,
  Masked,
  Menu,
  NativeSelect,
  PageHeader,
  Section,
  TextArea,
  TextField,
} from '../kit.js';
import { PlusIcon, XIcon } from 'lucide-react';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import type {
  PlatformRole,
  PlatformRoleAssignment,
  PlatformRoleAssignmentCreate,
  PlatformRoleAssignmentListItem,
  PlatformRoleRevocation,
} from '../api.js';
import { AdminCommandFeedback, useAdminFeedback } from '../components/admin-feedback.js';
import { useSubmitBlocked } from '../components/form.js';
import { AuditTrailLink } from '../components/audit-trail-link.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { LoginLockAction } from '../components/login-lock.js';
import { AdminListTable } from '../components/admin-list-table.js';
import { ListFooter, ListLoadMore } from '../components/list-footer.js';
import { ListEmpty, ListSkeleton } from '../components/list-state.js';
import { approvalSubmitLabel, canUse, executesApprovalDirectly } from '../permissions.js';
import {
  listStatus,
  type RoleAssignmentFilters,
  setupKeys,
  useRoleAssignments,
  useRoleAssignmentsSummary,
} from '../queries.js';
import { reasonSchema } from '../validation.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * «دسترسی‌های Platform Admin» on «سیاست‌ها و دسترسی» (prototype policies page, FTD §9.14): the fixed
 * platform roles with their live counts (S9), and for a Super Admin the role assignments (R1), grant
 * (`createPlatformRoleAssignment`), and revoke (R2). No role change is shown before the server
 * confirmed it. Every grant by a Super Admin returns `201` with `approvalRequestId` null (D-61,
 * §19.2.4). A `202` is still shown as «برای تأیید ارسال شد» if the server sends one.
 */

/** The staff roles in the AC §6.3 order. `MARKETER` grants arrive with Stage 6 (AC §18.13). */
const STAFF_ROLES = [
  'SUPER_ADMIN',
  'FINANCE_ADMIN',
  'OPERATIONS_ADMIN',
  'MARKETING_ADMIN',
  'AUDITOR',
] as const satisfies readonly PlatformRole[];

/** The approver ("sensitive") roles of AC §18.20.1: their grant goes to another Super Admin (D-49). */
const APPROVER_ROLES: ReadonlySet<string> = new Set(['SUPER_ADMIN', 'FINANCE_ADMIN']);

const ACCESS_ROLES: readonly PlatformRole[] = [...STAFF_ROLES, 'MARKETER'];

/** The prototype «دسترسی‌های Platform Admin» panel; «کاربر فعال» only for a Super Admin (S9). */
export function PlatformAccessSection({ self }: { readonly self: AdminSelf }) {
  const showCounts = canUse(self, 'getPlatformRoleAssignmentsSummary');
  const summary = useRoleAssignmentsSummary(showCounts);
  const counts = new Map(
    (summary.data?.activeAssignmentCounts ?? []).map((item) => [item.role, item.count]),
  );
  return (
    <Section
      eyebrow={`${formatCount(ACCESS_ROLES.length)} نقش ثابت`}
      id="platform-access"
      title="دسترسی‌های پلتفرم"
    >
      <AdminListTable<PlatformRole>
        caption="دسترسی نقش‌های پلتفرم"
        columns={[
          {
            enableHiding: false,
            header: 'نقش',
            id: 'role',
            render: (role) => <strong>{statusLabel('fixedRole', role)}</strong>,
          },
          ...(showCounts
            ? [
                {
                  enableHiding: false,
                  header: 'کاربر فعال',
                  id: 'count',
                  numeric: true,
                  render: (role: PlatformRole) => {
                    if (summary.status === 'pending') return '…';
                    const count = counts.get(role);
                    return count === undefined ? '—' : formatCount(count);
                  },
                },
              ]
            : []),
        ]}
        mobileSummary={(role) => ({
          primary: statusLabel('fixedRole', role),
          secondary:
            !showCounts || summary.status === 'pending'
              ? '…'
              : counts.get(role) === undefined
                ? '—'
                : formatCount(counts.get(role) ?? 0),
        })}
        regionLabel="جدول دسترسی نقش‌ها"
        rowKey={(role) => role}
        rows={ACCESS_ROLES}
      />
      {showCounts && summary.status === 'error' ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------
// R1 list, grant, revoke (Super Admin only)

function useInvalidateRoles() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.roleAssignments }),
      queryClient.invalidateQueries({ queryKey: setupKeys.summaries }),
      queryClient.invalidateQueries({ queryKey: setupKeys.approvals }),
    ]);
}

const revokeSchema = z.object({ reason: reasonSchema() });

function RevokeDialog({
  assignment,
  onOpenChange,
}: {
  readonly assignment: PlatformRoleAssignmentListItem;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const feedback = useAdminFeedback();
  const invalidate = useInvalidateRoles();
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<{ reason: string }>({
    defaultValues: { reason: '' },
    resolver: zodResolver(revokeSchema),
  });
  const command = useCommand<PlatformRoleRevocation, PlatformRoleAssignment>(
    'platform-role.revoke',
    (client, body, intent) =>
      client.post(
        `/admin/platform-role-assignments/${encodeURIComponent(assignment.id)}/revocation`,
        { body, intent },
      ),
    {
      onSuccess: async (result) => {
        await invalidate();
        feedback.success(`نقش «${statusLabel('fixedRole', result.data.role)}» لغو شد`);
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<PlatformRoleAssignment>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(applyFieldErrors(outcome.error, ['reason'], form.setError));
    if (outcome.error.code === 'STATE_CONFLICT' || outcome.error.code === 'LAST_SUPER_ADMIN') {
      await invalidate();
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const minimum =
    isApiError(error) && error.code === 'LAST_SUPER_ADMIN' ? error.details?.minimum : undefined;

  return (
    <Dialog
      description="نقش بلافاصله لغو می‌شود و کاربر در همهٔ دستگاه‌ها از پنل مدیریت خارج می‌شود. این کار قابل برگشت نیست؛ برای دسترسی دوباره باید دوباره به او نقش داده شود."
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title={`لغو نقش «${statusLabel('fixedRole', assignment.role)}»`}
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          await handle(await command.submit({ reason: values.reason.trim() }));
        })}
      >
        <p className="admin-muted">
          {`${assignment.user.displayNameFa ?? 'بدون نام'} — `}
          <Masked kind="mobile" value={assignment.user.mobileMasked} />
        </p>
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل لغو"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="platform." />
        <FieldErrorList errors={unmapped} />
        <AdminCommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        {typeof minimum === 'number' ? (
          <Banner tone="warning">
            {`دست‌کم ${formatCount(minimum)} مدیر ارشد فعال باید بماند؛ پیش از لغو، نقش مدیر ارشد را به فرد دیگری بدهید.`}
          </Banner>
        ) : null}
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
          variant="danger"
        >
          لغو نقش
        </Button>
      </form>
    </Dialog>
  );
}

const grantSchema = z
  .object({
    email: z.string().trim().max(254, 'حداکثر ۲۵۴ کاراکتر.'),
    firstNameFa: z.string().trim().max(100, 'حداکثر ۱۰۰ کاراکتر.'),
    lastNameFa: z.string().trim().max(100, 'حداکثر ۱۰۰ کاراکتر.'),
    mobileE164: z.string().transform(normalizeMobileField),
    reason: reasonSchema(),
    role: z.enum(STAFF_ROLES),
    userId: z.string(),
  })
  .superRefine((value, context) => {
    if (value.userId !== '') {
      return;
    }
    if (!MOBILE_E164_PATTERN.test(value.mobileE164)) {
      context.addIssue({
        code: 'custom',
        message: 'شماره موبایل را مانند ۰۹۱۲۱۲۳۴۵۶۷ وارد کنید.',
        path: ['mobileE164'],
      });
    }
    if (value.firstNameFa === '') {
      context.addIssue({ code: 'custom', message: 'نام الزامی است.', path: ['firstNameFa'] });
    }
    if (value.lastNameFa === '') {
      context.addIssue({
        code: 'custom',
        message: 'نام خانوادگی الزامی است.',
        path: ['lastNameFa'],
      });
    }
  });
type GrantInput = z.input<typeof grantSchema>;
type GrantValues = z.output<typeof grantSchema>;
const GRANT_FIELDS = [
  'role',
  'reason',
  'userId',
  'mobileE164',
  'firstNameFa',
  'lastNameFa',
  'email',
];

/** The request body (AC §17.4.5): exactly one of `userId` and `newUser`. */
export function toRoleAssignmentCreate(values: GrantValues): PlatformRoleAssignmentCreate {
  return {
    reason: values.reason,
    role: values.role,
    ...(values.userId !== ''
      ? { userId: values.userId }
      : {
          newUser: {
            firstNameFa: values.firstNameFa,
            lastNameFa: values.lastNameFa,
            mobileE164: values.mobileE164,
            ...(values.email === '' ? {} : { email: values.email }),
          },
        }),
  };
}

interface KnownUser {
  readonly displayNameFa: string | null;
  readonly id: string;
  readonly mobileMasked: string;
}

function userOptionLabel(user: KnownUser): string {
  return `${user.displayNameFa ?? 'بدون نام'} (${isolateLtr(displayServerMask(user.mobileMasked, 'mobile'))})`;
}

function GrantDialog({
  initialUserId,
  knownUsers,
  onOpenChange,
  self,
}: {
  readonly initialUserId: string | undefined;
  readonly knownUsers: readonly KnownUser[];
  readonly onOpenChange: (open: boolean) => void;
  readonly self: AdminSelf;
}) {
  const feedback = useAdminFeedback();
  const invalidate = useInvalidateRoles();
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<GrantInput, unknown, GrantValues>({
    defaultValues: {
      email: '',
      firstNameFa: '',
      lastNameFa: '',
      mobileE164: '',
      reason: '',
      role: 'OPERATIONS_ADMIN',
      userId: initialUserId ?? '',
    },
    resolver: zodResolver(grantSchema),
  });
  const role = form.watch('role');
  const userId = form.watch('userId');
  const [userQuery, setUserQuery] = useState(() => {
    const picked = knownUsers.find((user) => user.id === initialUserId);
    return picked === undefined ? '' : userOptionLabel(picked);
  });
  const [showNewUser, setShowNewUser] = useState(initialUserId === undefined);
  const command = useCommand<PlatformRoleAssignmentCreate, PlatformRoleAssignment>(
    'platform-role.grant',
    (client, body, intent) => client.post('/admin/platform-role-assignments', { body, intent }),
    {
      onApprovalPending: async (approval) => {
        await invalidate();
        feedback.approvalSubmitted(approval.approvalRequestId);
        onOpenChange(false);
      },
      onSuccess: async (result) => {
        await invalidate();
        feedback.success(`نقش «${statusLabel('fixedRole', result.data.role)}» داده شد`);
        onOpenChange(false);
      },
    },
  );

  function handle(outcome: CommandOutcome<PlatformRoleAssignment>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    setUnmapped(
      applyFieldErrors(
        error,
        GRANT_FIELDS,
        (name, fieldError, options) => form.setError(name as keyof GrantInput, fieldError, options),
        '',
      ).filter((fieldError) => !fieldError.field.startsWith('newUser.')),
    );
    for (const fieldError of error.fieldErrors) {
      const name = fieldError.field.replace(/^newUser\./, '');
      if (fieldError.field.startsWith('newUser.') && GRANT_FIELDS.includes(name)) {
        form.setError(name as keyof GrantInput, { message: fieldError.messageFa, type: 'server' });
      }
    }
  }

  const direct = executesApprovalDirectly(self);
  const knownOptions = knownUsers
    .filter((user) => user.id !== self.user.id)
    .map((user) => ({
      label: userOptionLabel(user),
      value: user.id,
    }));
  const sensitive = APPROVER_ROLES.has(role);
  const filteredUserOptions = knownOptions.filter((option) => {
    const needle = userQuery.trim();
    if (needle === '') {
      return true;
    }
    return option.label.includes(needle) || option.value.includes(needle);
  });

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="دادن نقش پلتفرم"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          handle(await command.submit(toRoleAssignmentCreate(values)));
        })}
      >
        <NativeSelect
          error={form.formState.errors.role?.message}
          label="نقش"
          options={STAFF_ROLES.map((value) => ({ label: statusLabel('fixedRole', value), value }))}
          {...form.register('role')}
        />
        {sensitive ? (
          <Banner tone="info">
            {direct
              ? 'این نقش تأییدکننده است و بلافاصله داده می‌شود.'
              : 'این نقش تأییدکننده است؛ دادن آن برای تأیید یک مدیر ارشد دیگر ارسال می‌شود و تا آن زمان اعمال نمی‌شود.'}
          </Banner>
        ) : null}
        <Combobox
          error={form.formState.errors.userId?.message}
          inputValue={userQuery}
          label="شماره موبایل یا نام"
          onChange={(next) => {
            form.setValue('userId', next, { shouldValidate: true });
            const picked = knownUsers.find((user) => user.id === next);
            setUserQuery(picked === undefined ? userQuery : userOptionLabel(picked));
            setShowNewUser(false);
          }}
          onInputValueChange={(next) => {
            setUserQuery(next);
            if (next.trim() === '') {
              form.setValue('userId', '', { shouldValidate: true });
              setShowNewUser(true);
              return;
            }
            const match = knownOptions.find((option) => option.label === next);
            if (match === undefined) {
              form.setValue('userId', '', { shouldValidate: true });
              setShowNewUser(true);
            }
          }}
          options={filteredUserOptions}
          placeholder="جست‌وجو در کاربران"
          value={userId === '' ? undefined : userId}
        />
        {showNewUser && userId === '' ? (
          <>
            <TextField
              autoComplete="off"
              error={form.formState.errors.mobileE164?.message}
              inputMode="tel"
              label="شماره موبایل"
              ltr
              placeholder="۰۹۱۲۱۲۳۴۵۶۷"
              {...form.register('mobileE164')}
            />
            <TextField
              error={form.formState.errors.firstNameFa?.message}
              label="نام"
              {...form.register('firstNameFa')}
            />
            <TextField
              error={form.formState.errors.lastNameFa?.message}
              label="نام خانوادگی"
              {...form.register('lastNameFa')}
            />
            <TextField
              error={form.formState.errors.email?.message}
              inputMode="email"
              label="ایمیل (اختیاری)"
              ltr
              {...form.register('email')}
            />
          </>
        ) : null}
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل دادن نقش"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="platform." />
        <FieldErrorList errors={unmapped} />
        <AdminCommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        <div className="admin-actions admin-actions--end">
          <Button
            disabled={offline.blocked || command.state.status === 'approval-pending'}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            {sensitive ? approvalSubmitLabel(self) : 'دادن نقش'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function readRoleFilters(role: string, status: string): RoleAssignmentFilters {
  return {
    ...(role === '' ? {} : { role }),
    ...(status === '' ? {} : { status }),
  };
}

export interface GroupedPlatformRoleUser {
  readonly assignments: readonly PlatformRoleAssignmentListItem[];
  readonly loginLock: PlatformRoleAssignmentListItem['loginLock'];
  readonly user: PlatformRoleAssignmentListItem['user'];
  readonly userId: string;
}

/** One table row per identity user (ADM-34). */
export function groupPlatformRoleUsers(
  rows: readonly PlatformRoleAssignmentListItem[],
): readonly GroupedPlatformRoleUser[] {
  const map = new Map<string, GroupedPlatformRoleUser>();
  for (const row of rows) {
    const existing = map.get(row.userId);
    if (existing === undefined) {
      map.set(row.userId, {
        assignments: [row],
        loginLock: row.loginLock,
        user: row.user,
        userId: row.userId,
      });
    } else {
      map.set(row.userId, { ...existing, assignments: [...existing.assignments, row] });
    }
  }
  return [...map.values()];
}

/** R1–R2 and the grant, for a Super Admin (`PLATFORM_ROLE_MANAGE`). */
export function RoleAssignmentsSection({ self }: { readonly self: AdminSelf }) {
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const assignments = useRoleAssignments(readRoleFilters(role, status));
  const rows = assignments.data?.pages.flatMap((page) => page.items) ?? [];
  const grouped = groupPlatformRoleUsers(rows);
  const [revoking, setRevoking] = useState<PlatformRoleAssignmentListItem | null>(null);
  const [granting, setGranting] = useState<{ readonly userId?: string } | null>(null);
  const canGrant = canUse(self, 'createPlatformRoleAssignment');
  const canRevoke = canUse(self, 'revokePlatformRoleAssignment');
  const knownUsers: KnownUser[] = [];
  for (const row of rows) {
    if (!knownUsers.some((user) => user.id === row.user.id)) {
      knownUsers.push(row.user);
    }
  }

  return (
    <Section
      actions={
        canGrant ? (
          <StockButton onClick={() => setGranting({})} size="sm" type="button">
            <PlusIcon aria-hidden="true" />
            دادن نقش
          </StockButton>
        ) : undefined
      }
      eyebrow="فقط مدیر ارشد"
      id="platform-role-assignments"
      title="انتساب نقش‌های پلتفرم"
    >
      <FilterPillBar
        activeFilterCount={(role === '' ? 0 : 1) + (status === 'ACTIVE' ? 0 : 1)}
        label="فیلتر نقش‌ها"
        onClearAll={() => {
          setRole('');
          setStatus('ACTIVE');
        }}
      >
        <FilterPill
          emptyLabel="همه نقش‌ها"
          label="نقش"
          onChange={setRole}
          options={[...STAFF_ROLES, 'MARKETER'].map((value) => ({
            label: statusLabel('fixedRole', value),
            value,
          }))}
          value={role}
        />
        <FilterPill
          emptyLabel="فعال"
          label="وضعیت"
          onChange={(value) => setStatus(value === '' ? 'ACTIVE' : value)}
          options={[
            { label: statusLabel('platformRoleAssignmentStatus', 'REVOKED'), value: 'REVOKED' },
          ]}
          value={status === 'ACTIVE' ? '' : status}
        />
      </FilterPillBar>
      <QueryState
        empty={<ListEmpty title="انتسابی با این فیلترها پیدا نشد" />}
        error={assignments.error}
        isEmpty={grouped.length === 0}
        onRetry={() => void assignments.refetch()}
        skeleton={<ListSkeleton label="در حال بارگذاری نقش‌ها…" />}
        status={listStatus(assignments)}
      >
        <AdminListTable
          caption="انتساب نقش‌های پلتفرم"
          columns={[
            {
              header: 'کاربر',
              id: 'user',
              pin: 'start',
              render: (row) => (
                <span className="saay-cell-main">
                  <strong>{row.user.displayNameFa ?? 'بدون نام'}</strong>
                  <small>
                    <Masked kind="mobile" value={row.user.mobileMasked} />
                  </small>
                  <small>
                    <AuditTrailLink
                      resourceId={row.user.id}
                      resourceType="IDENTITY_USER"
                      self={self}
                    />
                  </small>
                </span>
              ),
            },
            {
              header: 'نقش‌ها',
              id: 'roles',
              render: (row) => (
                <div className="flex flex-wrap items-center gap-1">
                  {row.assignments.map((assignment) => {
                    const removable =
                      assignment.status === 'ACTIVE' &&
                      assignment.userId !== self.user.id &&
                      canRevoke;
                    return (
                      <StatusBadge
                        key={assignment.id}
                        tone={assignment.status === 'ACTIVE' ? 'olive' : 'forest'}
                      >
                        {statusLabel('fixedRole', assignment.role)}
                        {removable ? (
                          <button
                            aria-label="حذف فیلتر"
                            className="inline-flex"
                            onClick={() => setRevoking(assignment)}
                            type="button"
                          >
                            <XIcon aria-hidden="true" className="size-3" />
                          </button>
                        ) : null}
                      </StatusBadge>
                    );
                  })}
                  {row.assignments.map((assignment) =>
                    assignment.approvalRequestId === null ? null : (
                      <small key={`approval-${assignment.id}`}>
                        <Link to={`/approvals/${assignment.approvalRequestId}`}>درخواست تأیید</Link>
                      </small>
                    ),
                  )}
                  {canGrant && row.userId !== self.user.id ? (
                    <StockButton
                      onClick={() => setGranting({ userId: row.userId })}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      + نقش
                    </StockButton>
                  ) : null}
                </div>
              ),
            },
            {
              header: <span className="sr-only">عملیات</span>,
              id: 'actions',
              render: (row) =>
                row.userId === self.user.id ? null : (
                  <Menu
                    label="عملیات کاربر"
                    trigger={
                      <Button aria-label="عملیات کاربر" size="sm" variant="ghost">
                        ⋯
                      </Button>
                    }
                  >
                    <LoginLockAction
                      loginLock={row.loginLock}
                      personLabel={row.user.displayNameFa ?? 'کاربر پلتفرم'}
                      presentation="menu"
                      self={self}
                      userId={row.userId}
                    />
                  </Menu>
                ),
            },
          ]}
          mobileSummary={(row) => ({
            primary: row.user.displayNameFa ?? 'بدون نام',
            secondary: row.assignments
              .map((assignment) => statusLabel('fixedRole', assignment.role))
              .join('، '),
          })}
          regionLabel="جدول انتساب نقش‌ها"
          rowKey={(row) => row.userId}
          rows={grouped}
        />
        {assignments.isFetchNextPageError ? (
          <ErrorState error={assignments.error} onRetry={() => void assignments.fetchNextPage()} />
        ) : null}
        <ListFooter
          pager={
            <ListLoadMore
              hasMore={assignments.hasNextPage}
              loading={assignments.isFetchingNextPage}
              onLoadMore={() => void assignments.fetchNextPage()}
            />
          }
          shown={rows.length}
          total={undefined}
        />
      </QueryState>
      {revoking === null ? null : (
        <RevokeDialog
          assignment={revoking}
          onOpenChange={(open) => {
            if (!open) setRevoking(null);
          }}
        />
      )}
      {granting === null ? null : (
        <GrantDialog
          initialUserId={granting.userId}
          knownUsers={knownUsers}
          onOpenChange={(open) => {
            if (!open) setGranting(null);
          }}
          self={self}
        />
      )}
    </Section>
  );
}

/** `/platform-access` (ADM-31): platform roles and assignments, separate from policies. */
export function PlatformAccessPage() {
  usePageTitle('دسترسی‌ها');
  return (
    <RequireArea area="platformAccess">
      {(self) => (
        <>
          <PageHeader title="دسترسی‌ها" />
          <PlatformAccessSection self={self} />
          {canUse(self, 'listPlatformRoleAssignments') ? (
            <RoleAssignmentsSection self={self} />
          ) : null}
        </>
      )}
    </RequireArea>
  );
}
